import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import colors from 'utils/colors';
import map from 'lodash/map';
import { NonIdealState } from '@blueprintjs/core';
import {
  TableRow,
  HeaderRow,
  TableHeader,
  TableData,
} from 'components/styled-components';
import filter from 'lodash/filter';
import AddContact from 'components/vendors/addContact';
import { InputGroup } from '@blueprintjs/core';
import hasPermission from 'utils/hasPermission';
import Permissions from 'utils/permissions';
import shortid from 'shortid';

const filterByMultiplePropertiesCaseInsensitive = (
  array,
  value,
  propertyNames
) => {
  return filter(array, element => {
    return propertyNames.some(property => {
      return element[property]
        ? element[property].toLowerCase().indexOf(value.toLowerCase()) !== -1
        : false;
    });
  });
};

const Row = ({ data }) => (
  <TableRow key={data.userID}>
    <TableData>
      {data.firstName} {data.lastName}
    </TableData>
    <TableData>{data.email}</TableData>
  </TableRow>
);

const Contacts = ({ data, appUser, showAddButton, ...rest }) => {
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');
  const [filteredContacts, setFilteredContacts] = useState(data);

  useEffect(() => {
    if (query === '') setFilteredContacts(data);
  }, [data, query]);

  const handleSearch = event => {
    const { value } = event.target;
    setQuery(value);
    const contactsAfterQuery = filterByMultiplePropertiesCaseInsensitive(
      data,
      value,
      ['email', 'firstName', 'lastName']
    );
    setFilteredContacts(contactsAfterQuery);
  };

  return (
    <Container>
      <DrawerHeader>
        Contacts
        <span style={{ margin: '0.25rem' }}>
          <InputGroup
            onChange={handleSearch}
            value={query}
            placeholder="Search Contacts"
            leftIcon="search"
          />
        </span>
        {hasPermission(appUser.permissions, [
          Permissions.CREATE_THIRD_PARTY_USER,
          Permissions.CREATE_ALL_USERS,
        ]) &&
          showAddButton && (
            <AddContact
              addBtnLabel="Add User"
              drawerLabel="Add User"
              drawerBtnLabel="SAVE"
              icon="add"
              addMobileStyle={true}
              {...rest}
            >
              +
            </AddContact>
          )}
      </DrawerHeader>
      <Table>
        <thead>
          <HeaderRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Email</TableHeader>
          </HeaderRow>
        </thead>
        <tbody>
          {filteredContacts.length && showAll
            ? map(filteredContacts, d => (
                <Row data={d} key={shortid.generate()} />
              ))
            : filteredContacts
                .slice(0, 5)
                .map((d, i) => <Row data={d} key={shortid.generate()} />)}
          {!filteredContacts.length && (
            <tr>
              <TableData colSpan={2}>
                <NonIdealState
                  icon="info-sign"
                  description="There are no contacts associated with this Third Party."
                />
              </TableData>
            </tr>
          )}
        </tbody>
      </Table>
      {filteredContacts.length > 5 && (
        <ViewWrapper onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Show Less' : `View All (${filteredContacts.length})`}
        </ViewWrapper>
      )}
    </Container>
  );
};

const Container = styled.div`
  border-radius: 3px;
  width: 100%;
  margin: 0 1rem;
  border: 1px solid ${colors.muted};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-radius: 3px;
`;

const ViewWrapper = styled.div`
  display: flex;
  padding: 1rem;
  justify-content: center;
  border-top: 1px solid ${colors.muted};
  color: ${colors.primary};
  cursor: pointer;
`;

const DrawerHeader = styled.div`
  width: 100%;
  border-bottom: 1px solid ${colors.muted};
  border-radius: 2px 2px 0px 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 500;
  background-color: #fff;
`;

export default Contacts;
